import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { listInvoices, InvoiceResponse, createInvoice } from '../api/invoicesApi';
import Layout from '../components/Layout';
import { openPdf } from '../utils/pdf';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Concept',
  issued: 'Verzonden',
  pending_check: 'Toetsing loopt',
  insured: 'Verzekerd',
  paid: 'Betaald',
  claimed: 'Geclaimd',
  overdue: 'Vervallen',
  disputed: 'Betwist',
  archived: 'Gearchiveerd',
};

export const InvoicesPage: React.FC = () => {
  const navigate = useNavigate();
  const { accessToken, isAuthenticated } = useAuthStore();

  const [invoices, setInvoices] = useState<InvoiceResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      navigate('/login');
      return;
    }
    loadInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, accessToken, navigate]);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const data = await listInvoices(accessToken!);
      setInvoices(data.invoices);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Facturen laden mislukt');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvoice = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    try {
      const newInvoice = await createInvoice(accessToken!, {
        debtor_id: formData.get('debtor_id') as string,
        invoice_number: formData.get('invoice_number') as string,
        amount_net: parseFloat(formData.get('amount_net') as string),
        vat_percentage: parseFloat(formData.get('vat_percentage') as string) || 21,
        invoice_date: formData.get('invoice_date') as string,
        due_date: formData.get('due_date') as string,
        description: formData.get('description') as string,
      });
      setInvoices([newInvoice, ...invoices]);
      setShowCreateForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Factuur aanmaken mislukt');
    }
  };

  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      draft: 'bg-slate-100 text-slate-700',
      issued: 'bg-mfb-blue/10 text-mfb-blue',
      pending_check: 'bg-amber-100 text-amber-700',
      insured: 'bg-emerald-100 text-emerald-700',
      paid: 'bg-emerald-200 text-emerald-800',
      claimed: 'bg-red-100 text-red-700',
      overdue: 'bg-red-100 text-red-700',
      disputed: 'bg-orange-100 text-orange-700',
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const fmt = (n: number) =>
    new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n);

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl font-semibold text-mfb-blue">Facturen</h1>
          <p className="text-sm text-slate-500">Beheer uw facturen en hun verzekeringsstatus</p>
        </div>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="px-4 py-2 bg-mfb-orange hover:bg-mfb-orange-dark text-white rounded-lg text-sm font-semibold transition shadow-sm"
        >
          {showCreateForm ? 'Annuleren' : '+ Nieuwe factuur'}
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {showCreateForm && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
          <h2 className="font-display text-lg font-semibold text-mfb-blue mb-4">Nieuwe factuur</h2>
          <form onSubmit={handleCreateInvoice} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input type="text" name="invoice_number" placeholder="Factuurnummer" required
                className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-mfb-blue outline-none" />
              <input type="text" name="debtor_id" placeholder="Debiteur ID" required
                className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-mfb-blue outline-none" />
              <input type="number" name="amount_net" placeholder="Bedrag (excl. btw)" step="0.01" required
                className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-mfb-blue outline-none" />
              <input type="number" name="vat_percentage" placeholder="Btw %" step="0.01" defaultValue="21"
                className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-mfb-blue outline-none" />
              <input type="date" name="invoice_date" required
                className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-mfb-blue outline-none" />
              <input type="date" name="due_date" required
                className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-mfb-blue outline-none" />
            </div>
            <textarea name="description" placeholder="Omschrijving" rows={3}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-mfb-blue outline-none" />
            <button type="submit"
              className="px-4 py-2 bg-mfb-orange hover:bg-mfb-orange-dark text-white rounded-lg font-semibold transition shadow-sm">
              Factuur aanmaken
            </button>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-slate-500">Facturen laden...</div>
      ) : invoices.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-500">
          Nog geen facturen. Maak uw eerste factuur aan!
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Factuur</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Bedrag</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Dekking</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Vervaldatum</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Document</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 text-sm font-medium text-mfb-blue">{invoice.invoice_number}</td>
                    <td className="px-6 py-4 text-sm text-slate-700">{fmt(invoice.amount_gross)}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {invoice.coverage_percentage ?? 0}%{' '}
                      <span className="text-slate-400">({fmt(invoice.coverage_amount ?? 0)})</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {new Date(invoice.due_date).toLocaleDateString('nl-NL')}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                        {STATUS_LABELS[invoice.status] || invoice.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button onClick={() => openPdf(`/api/invoices/${invoice.id}/pdf`, accessToken!)}
                        className="text-sm font-medium text-mfb-orange hover:underline">📄 PDF</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default InvoicesPage;
